import { useState, useEffect, useRef } from 'react';
import { Send, AlertCircle, CheckCircle, Clock, Zap, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DebitoInmediatoTesting() {
  const [loading, setLoading] = useState(false);
  const [tokenSolicitado, setTokenSolicitado] = useState(false);
  const [tokenIngresado, setTokenIngresado] = useState('');
  const [resultado, setResultado] = useState(null);
  const [tiempoRestante, setTiempoRestante] = useState(null);
  const [historial, setHistorial] = useState([]);
  const tokenInputRef = useRef(null);

  // Cargar historial al montar
  useEffect(() => {
    cargarHistorial();
  }, []);

  // Temporizador de 2 minutos
  useEffect(() => {
    if (tokenSolicitado && tiempoRestante === null) {
      setTiempoRestante(120);
    }

    if (tiempoRestante !== null && tiempoRestante > 0) {
      const timer = setTimeout(() => {
        setTiempoRestante(tiempoRestante - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (tiempoRestante === 0) {
      const resultadoTimeout = {
        success: false,
        mensaje: '⏰ Tiempo agotado - El token ha expirado (2 minutos)',
        codigo: 'TIMEOUT',
        timestamp: new Date().toISOString()
      };
      setResultado(resultadoTimeout);
      agregarAlHistorial('cobro', resultadoTimeout);
    }
  }, [tiempoRestante, tokenSolicitado]);

  const cargarHistorial = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/super-admin/debito-inmediato/logs`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistorial(response.data || []);
    } catch (error) {
      console.error('Error cargando historial:', error);
      setHistorial([]);
    }
  };

  const agregarAlHistorial = async (tipo, data) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/super-admin/debito-inmediato/log`,
        { tipo, data, timestamp: new Date().toISOString() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      cargarHistorial();
    } catch (error) {
      console.error('Error guardando log:', error);
    }
  };

  const solicitarToken = async () => {
    setLoading(true);
    setResultado(null);
    setTokenIngresado('');
    setTiempoRestante(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/super-admin/debito-inmediato/request-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const resultado = { ...response.data, timestamp: new Date().toISOString() };
      setResultado(resultado);
      agregarAlHistorial('token', resultado);
      
      if (response.data.success) {
        setTokenSolicitado(true);
        setTimeout(() => {
          tokenInputRef.current?.focus();
        }, 100);
      }
    } catch (error) {
      const errorData = {
        success: false,
        mensaje: error.response?.data?.detail || 'Error al solicitar token',
        timestamp: new Date().toISOString()
      };
      setResultado(errorData);
      agregarAlHistorial('token', errorData);
    } finally {
      setLoading(false);
    }
  };

  const ejecutarCobro = async () => {
    if (!tokenIngresado || tokenIngresado.length !== 8) {
      alert('Por favor ingresa un token de 8 dígitos');
      return;
    }
    
    setLoading(true);
    setResultado(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/super-admin/debito-inmediato/execute-cobro?token_recibido=${tokenIngresado}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const resultado = { ...response.data, token_usado: tokenIngresado, timestamp: new Date().toISOString() };
      setResultado(resultado);
      agregarAlHistorial('cobro', resultado);
      setTiempoRestante(null);
      
      if (response.data.success || response.data.codigo === '100') {
        setTimeout(() => {
          setTokenSolicitado(false);
          setTokenIngresado('');
        }, 5000);
      }
    } catch (error) {
      const errorData = {
        success: false,
        mensaje: error.response?.data?.detail || 'Error al ejecutar cobro',
        token_usado: tokenIngresado,
        timestamp: new Date().toISOString()
      };
      setResultado(errorData);
      agregarAlHistorial('cobro', errorData);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && tokenIngresado.length === 8) {
      ejecutarCobro();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCodigoColor = (codigo) => {
    if (codigo === '0000') return 'bg-green-100 text-green-800';
    if (codigo === '0065') return 'bg-orange-100 text-orange-800';
    if (codigo === '0096') return 'bg-red-100 text-red-800';
    if (codigo === '100') return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold flex items-center gap-2" style={{ color: '#051C2C' }}>
          <Zap className="w-6 h-6 text-yellow-500" />
          Testing: Débito Inmediato
        </h3>
        {tiempoRestante !== null && tiempoRestante > 0 && (
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold text-lg ${
            tiempoRestante <= 30 ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-green-100 text-green-700'
          }`}>
            <Clock className="w-5 h-5" />
            {formatTime(tiempoRestante)}
          </div>
        )}
      </div>
      
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
        <p className="text-sm font-bold text-yellow-900">
          ⏰ IMPORTANTE: Token válido por 2 minutos y un solo uso
        </p>
        <p className="text-xs text-yellow-800 mt-1">
          Debes ingresar el token y ejecutar el cobro en menos de 2 minutos
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* PASO 1 */}
        <div className="bg-white rounded-lg border-2 border-blue-300 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
              1
            </div>
            <h4 className="font-bold text-lg">Solicitar Token SMS</h4>
          </div>
          
          <div className="space-y-3">
            <div className="bg-gray-50 rounded p-3 text-sm">
              <p className="font-medium text-gray-700">📱 SMS se enviará a:</p>
              <p className="font-mono font-bold text-lg text-blue-600">04142899874</p>
            </div>
            
            <div className="bg-gray-50 rounded p-3 text-xs text-gray-600">
              <p><strong>Datos de prueba:</strong></p>
              <p>• Pagador: V00000000091390</p>
              <p>• Monto: 1.00 VES</p>
              <p>• A: CAURA TECH (Pooling)</p>
            </div>
            
            <button
              onClick={solicitarToken}
              disabled={loading}
              className="w-full py-3 text-white rounded-lg font-bold text-lg hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#003594' }}
            >
              {loading ? '⏳ Enviando...' : '📤 SOLICITAR TOKEN'}
            </button>
          </div>
        </div>

        {/* PASO 2 */}
        <div className={`bg-white rounded-lg border-2 p-5 ${
          tokenSolicitado ? 'border-green-400 shadow-lg' : 'border-gray-200 opacity-50'
        }`}>
          <div className="flex items-center gap-2 mb-3">
            <div className={`w-8 h-8 rounded-full ${
              tokenSolicitado ? 'bg-green-600 animate-pulse' : 'bg-gray-400'
            } text-white flex items-center justify-center font-bold`}>
              2
            </div>
            <h4 className="font-bold text-lg">Ejecutar Cobro</h4>
          </div>
          
          {!tokenSolicitado ? (
            <div className="text-center py-8 text-gray-400">
              <p className="text-sm">Primero solicita el token</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-300 rounded p-3">
                <p className="text-sm font-bold text-green-900 mb-2">
                  ✅ Token solicitado - Verifica SMS
                </p>
                <p className="text-xs text-green-700">
                  Token llega en ~10s a 04142899874
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Token de 8 dígitos:
                </label>
                <input
                  ref={tokenInputRef}
                  type="text"
                  maxLength="8"
                  placeholder="● ● ● ● ● ● ● ●"
                  value={tokenIngresado}
                  onChange={(e) => setTokenIngresado(e.target.value.replace(/\D/g, ''))}
                  onKeyPress={handleKeyPress}
                  disabled={loading || tiempoRestante === 0}
                  className="w-full px-4 py-3 border-2 border-blue-400 rounded-lg text-center text-2xl font-mono font-bold focus:ring-4 focus:ring-blue-200 disabled:opacity-50"
                  autoComplete="off"
                />
                <p className="text-xs text-gray-500 mt-1 text-center">
                  Presiona Enter para ejecutar
                </p>
              </div>
              
              <button
                onClick={ejecutarCobro}
                disabled={loading || !tokenIngresado || tokenIngresado.length !== 8 || tiempoRestante === 0}
                className="w-full py-3 bg-green-600 text-white rounded-lg font-bold text-lg hover:bg-green-700 disabled:opacity-50 transform hover:scale-105"
              >
                {loading ? '⏳ Ejecutando...' : '✓ EJECUTAR COBRO AHORA'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Resultado Actual */}
      {resultado && (
        <div className={`mb-6 p-5 rounded-lg border-2 ${
          resultado.success ? 'bg-green-50 border-green-400' : 
          resultado.codigo === 'TIMEOUT' ? 'bg-orange-50 border-orange-400' :
          'bg-red-50 border-red-400'
        }`}>
          <div className="flex items-start gap-3">
            {resultado.success ? (
              <CheckCircle className="w-8 h-8 text-green-600" />
            ) : (
              <AlertCircle className="w-8 h-8 text-red-600" />
            )}
            <div className="flex-1">
              <h4 className={`font-bold text-lg ${
                resultado.success ? 'text-green-900' : 
                resultado.codigo === 'TIMEOUT' ? 'text-orange-900' : 
                'text-red-900'
              }`}>
                {resultado.success ? '🎉 ¡Cobro Exitoso!' : 
                 resultado.codigo === 'TIMEOUT' ? '⏰ Tiempo Agotado' :
                 '❌ Error'}
              </h4>
              <p className="text-sm mt-2 font-medium">{resultado.mensaje}</p>
              {resultado.codigo && resultado.codigo !== 'TIMEOUT' && (
                <div className="mt-3 p-3 bg-white rounded border">
                  <p className="text-xs font-mono">
                    <strong>Código:</strong> {resultado.codigo} | <strong>Descripción:</strong> {resultado.descripcion}
                  </p>
                  {resultado.token_usado && (
                    <p className="text-xs mt-1"><strong>Token:</strong> {resultado.token_usado}</p>
                  )}
                </div>
              )}
              
              {!resultado.success && (
                <button
                  onClick={() => {
                    setTokenSolicitado(false);
                    setTokenIngresado('');
                    setResultado(null);
                    setTiempoRestante(null);
                    solicitarToken();
                  }}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                >
                  🔄 Solicitar Nuevo Token
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Historial */}
      <div className="bg-white rounded-lg border-2 border-gray-300 p-5">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Historial de Pruebas
          </h4>
          <button
            onClick={cargarHistorial}
            className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200"
          >
            🔄 Actualizar
          </button>
        </div>
        
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {historial.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm">No hay pruebas registradas</p>
          ) : (
            historial.slice().reverse().map((log, idx) => {
              const logData = log.data || log.metadata || {};
              return (
              <div key={idx} className="p-3 bg-gray-50 rounded border hover:bg-gray-100">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                        log.tipo === 'token' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                      }`}>
                        {log.tipo === 'token' ? 'TokenDI' : log.service || 'CobroDI'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(log.timestamp || log.created_at).toLocaleString('es-VE')}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 mt-2">
                      {logData.success || log.level === 'INFO' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-600" />
                      )}
                      <p className="text-sm">{logData.mensaje || logData.descripcion || log.message || 'Sin mensaje'}</p>
                    </div>
                    
                    {(logData.codigo || (logData.api_response && logData.api_response.codigo)) && (
                      <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-mono ${getCodigoColor(logData.codigo || logData.api_response?.codigo)}`}>
                        Código: {logData.codigo || logData.api_response?.codigo}
                      </span>
                    )}
                    
                    {logData.token_usado && (
                      <p className="text-xs text-gray-600 mt-1 font-mono">Token: {logData.token_usado}</p>
                    )}
                  </div>
                  
                  <details className="text-xs">
                    <summary className="cursor-pointer text-gray-500 hover:text-gray-700">JSON</summary>
                    <pre className="mt-2 p-2 bg-white rounded border text-xs overflow-x-auto max-w-md">
                      {JSON.stringify(logData.response_completa || logData, null, 2)}
                    </pre>
                  </details>
                </div>
              </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
